import ActionMenu from '@/components/common/ActionMenu';
import { useBalanceChangeDateRangeState } from '@/components/common/BalanceChange';
import { Button, MenuButton } from '@/components/common/Button';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import HistoricalBalanceChange from '@/components/common/HistoricalBalanceChange';
import {
    ChevronDown,
    MinusCircle,
    PlusCircle,
    SwitchHorizontal01,
    Trash01,
    TypeSquare,
} from '@/components/common/icons';
import Icon from '@/components/common/icons/Icon';
import { Text, Title } from '@/components/common/Typography';
import { Divider, FlexProps, MenuItem, Skeleton } from '@chakra-ui/react';
import { isPlentyGoalAccount } from '@common/financialEntities';
import { Goal } from '@common/goals';
import {
    getGoalAccountBalance,
    getGoalAccountOwnershipDetails,
    getGoalBalanceForPageMode,
    infoByGoalAccountStructure,
} from '@common/react/goals';
import { usePageContext } from '@common/react/pageMode';
import { useCurrentUser } from '@common/react/user';
import { toDollars } from '@common/util/currency';
import { showGoalDeleteModal } from '../GoalDeleteModal';
import { showGoalRenameModal } from '../GoalRenameModal';
import { useStartGoalDepositFlow } from '../helpers';
import { showOnetimeGoalToGoalFlowModal } from '../OnetimeGoalToGoalFlowModal';
import { showOnetimeGoalWithdrawalFlowModal } from '../OnetimeGoalWithdrawalFlowModal';
import { useGoalDetailContext } from './GoalDetailContext';
import { getActionableGoalAccountForCurrentUser } from './helpers';

export default function GoalTitleSection(props: FlexProps) {
    return (
        <FlexRow
            px={16}
            h={18}
            justify="space-between"
            align="flex-end"
            {...props}
        >
            <FlexRow align="flex-end" gap={4}>
                <GoalTitle />
                <GoalBalanceChange />
            </FlexRow>

            <GoalActionMenu />
        </FlexRow>
    );
}

function GoalTitle(props: FlexProps) {
    const { pageMode } = usePageContext();
    const { goalLoading, goalUnfiltered } = useGoalDetailContext();

    if (goalLoading || !goalUnfiltered) {
        return <Skeleton w={40} h={8} />;
    }

    const details = getGoalAccountOwnershipDetails(goalUnfiltered);
    if (!details) return <></>;

    const { balance, suffixBalance } = getGoalBalanceForPageMode({
        details,
        pageMode,
    });

    const suffix = suffixBalance ? (
        <Title color="gray.300" lineHeight={1}>{` of ${toDollars(
            suffixBalance,
            {
                withCents: true,
            }
        )}`}</Title>
    ) : (
        <></>
    );

    return (
        <FlexRow {...props} gap={2}>
            <Title lineHeight={1}>
                {toDollars(balance, { withCents: true })}
            </Title>
            {suffix}
        </FlexRow>
    );
}

function GoalBalanceChange(props: FlexProps) {
    const { goalLoading, goal } = useGoalDetailContext();
    const [balanceChangeDateRange, setBalanceChangeDateRange] =
        useBalanceChangeDateRangeState();

    if (goalLoading || !goal) {
        return <Skeleton w={20} h={4} />;
    }

    return (
        <HistoricalBalanceChange
            {...props}
            dateRange={balanceChangeDateRange}
            onChangeDateRange={setBalanceChangeDateRange}
            positiveAccounts={goal.financialEntities.filter(
                isPlentyGoalAccount
            )}
        />
    );
}

function GoalActionMenu() {
    const { activePartnershipId, featureFlags } = useCurrentUser();
    const { goalUnfiltered, onClose } = useGoalDetailContext();

    const goalAccount = getActionableGoalAccountForCurrentUser(goalUnfiltered);
    const startGoalDepositFlow = useStartGoalDepositFlow(goalUnfiltered);

    const isMoneyMovementEnabled = featureFlags.isMoneyMovementEnabled;
    const shouldShowOnetimeTransfer =
        goalAccount?.info.investmentType === 'HIGH_YIELD_CASH';
    const shouldShowOnetimeWithdrawal =
        goalAccount &&
        // hide if Atomic account is closing
        !goalAccount.closingExternalAccountMask &&
        // hide for Plenty IRAs
        !['TRADITIONAL_IRA', 'ROTH_IRA'].includes(goalAccount.info.accountType);
    const shouldShowWithdrawAllAndDelete =
        goalAccount &&
        // hide if Atomic account is closing
        !goalAccount.closingExternalAccountMask;

    if (!goalUnfiltered) {
        return <Skeleton w={40} h={8} />;
    }
    if (!goalAccount) return <></>;

    const balance = getGoalAccountBalance(goalAccount) ?? 0;
    const goalId = goalUnfiltered.id;
    const accountId = goalAccount.id;

    const handleOnetimeWithdrawal = () => {
        if (!isMoneyMovementEnabled || !balance) return;
        showOnetimeGoalWithdrawalFlowModal({ goalId, sourceId: accountId });
    };

    const handleOnetimeTransfer = () => {
        if (!isMoneyMovementEnabled || !balance) return;
        showOnetimeGoalToGoalFlowModal({ goalId, sourceId: accountId });
    };

    return (
        <ActionMenu
            menuButton={
                <MenuButton
                    as={Button}
                    colorScheme="dark"
                    size="sm"
                    w={44}
                    textAlign="left"
                    rightIcon={<ChevronDown size="sm" />}
                    rounded="full"
                    onClickEvent="Goal Action Menu Button Clicked"
                >
                    Account actions
                </MenuButton>
            }
        >
            <GoalAccountStructureExplainer
                goal={goalUnfiltered}
                activePartnershipId={activePartnershipId}
            />
            <FlexCol gap={1.5} mt={4}>
                {startGoalDepositFlow && (
                    <MenuItem
                        disabled={!isMoneyMovementEnabled}
                        icon={<PlusCircle />}
                        onClick={startGoalDepositFlow}
                    >
                        Deposit money
                    </MenuItem>
                )}
                {shouldShowOnetimeWithdrawal ? (
                    <MenuItem
                        isDisabled={!isMoneyMovementEnabled || !balance}
                        icon={<MinusCircle />}
                        onClick={handleOnetimeWithdrawal}
                    >
                        Withdraw money
                    </MenuItem>
                ) : null}
                {shouldShowOnetimeTransfer ? (
                    <MenuItem
                        isDisabled={!isMoneyMovementEnabled || !balance}
                        icon={<SwitchHorizontal01 />}
                        onClick={handleOnetimeTransfer}
                    >
                        Transfer between goals
                    </MenuItem>
                ) : null}
                <MenuItem
                    icon={<TypeSquare />}
                    onClick={() =>
                        showGoalRenameModal({
                            goal: goalUnfiltered,
                        })
                    }
                >
                    Rename
                </MenuItem>
                {shouldShowWithdrawAllAndDelete && (
                    <MenuItem
                        icon={<Trash01 />}
                        onClick={() => {
                            showGoalDeleteModal({
                                goal: goalUnfiltered,
                                onComplete: onClose,
                            });
                        }}
                    >
                        Withdraw all & delete goal
                    </MenuItem>
                )}
            </FlexCol>
        </ActionMenu>
    );
}

function GoalAccountStructureExplainer({
    goal,
    activePartnershipId,
    ...props
}: {
    goal: Goal;
    activePartnershipId: string | null | undefined;
} & FlexProps) {
    const type = goal.accountStructure;
    if (!type) return <></>;
    const info = infoByGoalAccountStructure[type];
    const description = info.description({ activePartnershipId });

    return (
        <>
            <FlexCol px={5} pb={5} maxW={60} {...props}>
                <Text fontSize="2xs" letterSpacing="widest" color="gray.300">
                    THIS IS A
                </Text>
                <FlexRow align="center" gap={2} py={1}>
                    <Icon name={info.iconName} size={10} color="white" />
                    <Text color="white">{info.title} account</Text>
                </FlexRow>
                <Text fontSize="sm" color="gray.300" lineHeight="short">
                    {description}
                </Text>
            </FlexCol>
            <Divider borderColor="blackAlpha.800" />
        </>
    );
}
